import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAdminProduct,
  deleteAdminProduct,
  deleteAdminProductImage,
  fetchAdminProduct,
  fetchAdminProductCategories,
  fetchAdminProductSummary,
  fetchAdminProducts,
  updateAdminProduct,
  uploadAdminProductImages,
} from '../apis/adminProduct';
import type { AdminProductBody, AdminProductParams } from '../types/adminProduct.types';

const PRODUCTS_KEY = ['admin-products'];

/** Pengaturan Produk total cards. */
export const useAdminProductSummary = () =>
  useQuery({ queryKey: [...PRODUCTS_KEY, 'summary'], queryFn: fetchAdminProductSummary });

/** Daftar Produk; the previous page stays on screen while the next one loads. */
export const useAdminProducts = (params: AdminProductParams) =>
  useQuery({
    queryKey: [...PRODUCTS_KEY, 'list', params],
    queryFn: () => fetchAdminProducts(params),
    placeholderData: keepPreviousData,
  });

/** Kategori dropdown; categories rarely change. */
export const useAdminProductCategories = () =>
  useQuery({
    queryKey: [...PRODUCTS_KEY, 'categories'],
    queryFn: fetchAdminProductCategories,
    staleTime: 5 * 60 * 1000,
  });

/** Detail Produk; nothing is requested without an id (Tambah Produk). */
export const useAdminProduct = (productId: string | undefined) =>
  useQuery({
    queryKey: [...PRODUCTS_KEY, 'detail', productId],
    queryFn: () => fetchAdminProduct(productId as string),
    enabled: Boolean(productId),
  });

export interface SaveProductInput {
  /** undefined creates a new book */
  productId?: string;
  body: AdminProductBody;
  /** Photos to upload after the product is saved */
  newFiles: File[];
  /** Saved photos the admin removed */
  removedImageIds: string[];
}

export interface SaveProductResult {
  id: string;
  /** The fields were saved but a photo change failed (e.g. storage unavailable) */
  photoError: string | null;
}

/** Simpan / Tambah: saves the fields, then uploads new photos and removes deleted ones, so Batal
 *  leaves photos untouched. A failed field save rejects; a failed photo change is reported in
 *  `photoError`, since the product itself is saved by then. */
export const useSaveAdminProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      body,
      newFiles,
      removedImageIds,
    }: SaveProductInput): Promise<SaveProductResult> => {
      const saved = productId
        ? await updateAdminProduct(productId, body)
        : await createAdminProduct(body);
      try {
        for (const imageId of removedImageIds) {
          await deleteAdminProductImage(saved.id, imageId);
        }
        if (newFiles.length > 0) {
          await uploadAdminProductImages(saved.id, newFiles);
        }
      } catch (error) {
        return { id: saved.id, photoError: (error as Error).message };
      }
      return { id: saved.id, photoError: null };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

export const useDeleteAdminProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => deleteAdminProduct(productId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};
